// src/components/TaskCalendar/TaskModal.tsx
import React, { useState, useEffect } from 'react';
import {
  X, Plus, User, Paperclip, Trash2, Upload, CheckCircle, XCircle, RotateCcw,
  Calendar, Clock, MessageSquare
} from 'lucide-react';
import { assignedTasksApi, AssignedTask } from '../../api/assignedTasks';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/client';

type TaskStatus = AssignedTask['status'];

interface TaskModalProps {
  date: Date | string;
  isOpen: boolean;
  onClose: () => void;
  onTaskUpdate: () => void;
}

const statusLabels: Record<TaskStatus, string> = {
  new: 'Новая',
  in_progress: 'В работе',
  pending_review: 'На согласовании',
  completed: 'Выполнена',
  rejected: 'Отклонена',
  revision: 'На доработке',
};

const statusColors: Record<TaskStatus, string> = {
  new: '#faad14',
  in_progress: '#2575fc',
  pending_review: '#9b59b6',
  completed: '#28a745',
  rejected: '#ff4d4f',
  revision: '#ff8c00',
};

interface User {
  id: string;
  name: string;
  email: string;
}

function toLocalDateString(dateInput: Date | string): string {
  if (typeof dateInput === 'string') return dateInput;
  const year = dateInput.getFullYear();
  const month = String(dateInput.getMonth() + 1).padStart(2, '0');
  const day = String(dateInput.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const TaskModal: React.FC<TaskModalProps> = ({
  date,
  isOpen,
  onClose,
  onTaskUpdate,
}) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<{
    assignedToMe: AssignedTask[];
    createdByMe: AssignedTask[];
  }>({ assignedToMe: [], createdByMe: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'assigned' | 'created'>('assigned');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    assigneeId: '',
    dueDate: '',
    attachments: [] as File[],
  });
  const [attachmentsPreview, setAttachmentsPreview] = useState<
    { name: string; size: number }[]
  >([]);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewTaskId, setReviewTaskId] = useState<string | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewAttachments, setReviewAttachments] = useState<File[]>([]);
  const [reviewAttachmentsPreview, setReviewAttachmentsPreview] = useState<
    { name: string; size: number }[]
  >([]);

  // Исправлено: тип 'rejected' | 'revision'
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentAction, setCommentAction] = useState<'rejected' | 'revision'>('revision');
  const [commentTaskId, setCommentTaskId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');

  const dateStr = toLocalDateString(date);

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
      fetchUsers();
      setForm((prev) => ({ ...prev, dueDate: dateStr }));
    }
  }, [isOpen, dateStr]);

  const getUserName = (id: string) => {
    const u = users.find((u) => u.id === id);
    return u ? u.name : 'Неизвестно';
  };

  const fetchTasks = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [assignedRes, createdRes] = await Promise.all([
        assignedTasksApi.getTasks({ assigneeId: user.id, dueDate: dateStr }),
        assignedTasksApi.getTasks({ creatorId: user.id, dueDate: dateStr }),
      ]);

      const enrichObservers = (tasks: AssignedTask[]): AssignedTask[] =>
        tasks.map((task) => ({
          ...task,
          observers: task.observers.map((obs) => ({
            ...obs,
            name: getUserName(obs.id),
          })),
        }));

      setTasks({
        assignedToMe: enrichObservers(assignedRes.data),
        createdByMe: enrichObservers(createdRes.data),
      });
    } catch (err) {
      console.error('Ошибка загрузки задач:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/company-users');
      setUsers(res.data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
    }
  };

  // ---------- Работа с файлами ----------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setForm((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
    setAttachmentsPreview((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name, size: f.size })),
    ]);
  };

  const removeAttachment = (index: number) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
    setAttachmentsPreview((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------- Создание задачи ----------
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!form.title.trim() || !form.assigneeId) {
      alert('Заполните заголовок и выберите исполнителя');
      return;
    }
    try {
      const attachments = await Promise.all(
        form.attachments.map(
          (file) =>
            new Promise<{
              name: string;
              data: string;
              size: number;
              mimeType: string;
            }>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) =>
                resolve({
                  name: file.name,
                  data: event.target?.result as string,
                  size: file.size,
                  mimeType: file.type,
                });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );

      await assignedTasksApi.createTask({
        title: form.title,
        description: form.description,
        assigneeId: form.assigneeId,
        dueDate: form.dueDate,
        attachments,
      });

      setShowCreateForm(false);
      setForm({
        title: '',
        description: '',
        assigneeId: '',
        dueDate: dateStr,
        attachments: [],
      });
      setAttachmentsPreview([]);
      await fetchTasks();
      onTaskUpdate();
    } catch (err: any) {
      console.error('❌ Ошибка создания задачи:', err);
      alert(`Ошибка: ${err.response?.data?.error || err.message}`);
    }
  };

  // ---------- Согласование ----------
  const openReviewModal = (taskId: string) => {
    setReviewTaskId(taskId);
    setReviewComment('');
    setReviewAttachments([]);
    setReviewAttachmentsPreview([]);
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setReviewTaskId(null);
    setReviewComment('');
    setReviewAttachments([]);
    setReviewAttachmentsPreview([]);
  };

  const handleReviewFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setReviewAttachments((prev) => [...prev, ...files]);
    setReviewAttachmentsPreview((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name, size: f.size })),
    ]);
  };

  const removeReviewAttachment = (index: number) => {
    setReviewAttachments((prev) => prev.filter((_, i) => i !== index));
    setReviewAttachmentsPreview((prev) =>
      prev.filter((_, i) => i !== index)
    );
  };

  const handleSendForReview = async () => {
    if (!reviewTaskId) return;
    try {
      const attachments = await Promise.all(
        reviewAttachments.map(
          (file) =>
            new Promise<{
              name: string;
              data: string;
              size: number;
              mimeType: string;
            }>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) =>
                resolve({
                  name: file.name,
                  data: event.target?.result as string,
                  size: file.size,
                  mimeType: file.type,
                });
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );
      await assignedTasksApi.updateTask(reviewTaskId, {
        status: 'pending_review',
        completionComment: reviewComment,
        attachments,
      });
      await fetchTasks();
      onTaskUpdate();
      closeReviewModal();
    } catch (err) {
      console.error('Ошибка отправки на согласование:', err);
      alert('Не удалось отправить задачу на согласование');
    }
  };

  // ---------- Действия ----------
  const handleStatusChange = async (
    taskId: string,
    newStatus: TaskStatus,
    comment?: string
  ) => {
    try {
      await assignedTasksApi.updateTask(taskId, {
        status: newStatus,
        completionComment: comment,
      });
      await fetchTasks();
      onTaskUpdate();
    } catch (err) {
      console.error('Ошибка обновления статуса:', err);
      alert('Не удалось обновить статус');
    }
  };

  // Открыть красивое модальное окно для reject/revision
  const openCommentModal = (taskId: string, action: 'reject' | 'revision') => {
    const statusAction = action === 'reject' ? 'rejected' : 'revision';
    setCommentTaskId(taskId);
    setCommentAction(statusAction);
    setCommentText('');
    setShowCommentModal(true);
  };

  const submitCommentAction = async () => {
    if (!commentTaskId) return;
    await handleStatusChange(commentTaskId, commentAction, commentText);
    setShowCommentModal(false);
    setCommentTaskId(null);
    setCommentText('');
  };

  const handleReview = async (
    taskId: string,
    action: 'approve' | 'reject' | 'revision',
    comment?: string
  ) => {
    let newStatus: TaskStatus;
    switch (action) {
      case 'approve': newStatus = 'completed'; break;
      case 'reject': newStatus = 'rejected'; break;
      case 'revision': newStatus = 'revision'; break;
      default: return;
    }
    await handleStatusChange(taskId, newStatus, comment);
  };

  // Скачивание вложения
  const downloadFile = (attachment: {
    name: string;
    url: string;
    size: number;
    mimeType: string;
  }) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  const displayDate = dateStr.split('-').reverse().join('.');

  return (
    <div className="task-modal-container" style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            <Calendar size={20} style={{ marginRight: 8 }} />
            Задачи на {displayDate}
          </h3>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        {!showCreateForm ? (
          <>
            <div style={styles.tabs}>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'assigned' ? styles.activeTab : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('assigned');
                }}
              >
                Мои задачи ({tasks.assignedToMe.length})
              </button>
              <button
                style={{
                  ...styles.tab,
                  ...(activeTab === 'created' ? styles.activeTab : {}),
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('created');
                }}
              >
                Я поставил ({tasks.createdByMe.length})
              </button>
            </div>

            <div style={styles.content}>
              {loading ? (
                <p style={styles.empty}>Загрузка...</p>
              ) : (
                <>
                  {activeTab === 'assigned' &&
                    (tasks.assignedToMe.length === 0 ? (
                      <p style={styles.empty}>Нет задач</p>
                    ) : (
                      tasks.assignedToMe.map((task) => (
                        <div key={task.id} style={styles.taskCard}>
                          <div style={styles.taskHeader}>
                            <strong>{task.title}</strong>
                            <span
                              style={{
                                ...styles.statusBadge,
                                backgroundColor:
                                  statusColors[task.status] + '22',
                                color: statusColors[task.status],
                                border: `1px solid ${
                                  statusColors[task.status]
                                }44`,
                              }}
                            >
                              {statusLabels[task.status]}
                            </span>
                          </div>
                          {task.description && (
                            <p style={styles.taskDesc}>
                              {task.description}
                            </p>
                          )}
                          <div style={styles.taskMeta}>
                            <span>
                              <User size={12} style={{ marginRight: 4 }} />
                              {task.creatorName}
                            </span>
                            <span>
                              <Clock size={12} style={{ marginRight: 4 }} />
                              {task.dueDate.split('-').reverse().join('.')}
                            </span>
                          </div>
                          {task.attachments.length > 0 && (
                            <div style={styles.taskAttachments}>
                              <Paperclip size={14} />
                              {task.attachments.map((att, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => downloadFile(att)}
                                  style={styles.attachmentLink}
                                >
                                  {att.name} (
                                  {(att.size / 1024).toFixed(1)} KB)
                                </button>
                              ))}
                            </div>
                          )}
                          <div style={styles.taskActions}>
                            {task.status === 'new' && (
                              <button
                                onClick={() =>
                                  handleStatusChange(
                                    task.id,
                                    'in_progress'
                                  )
                                }
                                style={styles.actionButton}
                              >
                                В работу
                              </button>
                            )}
                            {task.status === 'in_progress' && (
                              <button
                                onClick={() => openReviewModal(task.id)}
                                style={styles.actionButton}
                              >
                                На согласование
                              </button>
                            )}
                            {task.status === 'pending_review' &&
                              user?.id === task.creatorId && (
                                <div style={styles.reviewActions}>
                                  <button
                                    onClick={() =>
                                      handleReview(task.id, 'approve')
                                    }
                                    style={{
                                      ...styles.actionButton,
                                      backgroundColor: '#28a745',
                                    }}
                                  >
                                    <CheckCircle size={14} /> Принять
                                  </button>
                                  <button
                                    onClick={() =>
                                      openCommentModal(task.id, 'reject')
                                    }
                                    style={{
                                      ...styles.actionButton,
                                      backgroundColor: '#ff4d4f',
                                    }}
                                  >
                                    <XCircle size={14} /> Отклонить
                                  </button>
                                  <button
                                    onClick={() =>
                                      openCommentModal(task.id, 'revision')
                                    }
                                    style={{
                                      ...styles.actionButton,
                                      backgroundColor: '#faad14',
                                    }}
                                  >
                                    <RotateCcw size={14} /> Доработка
                                  </button>
                                </div>
                              )}
                            {task.status === 'revision' &&
                              user?.id === task.assigneeId && (
                                <button
                                  onClick={() =>
                                    handleStatusChange(
                                      task.id,
                                      'in_progress'
                                    )
                                  }
                                  style={styles.actionButton}
                                >
                                  Доработать
                                </button>
                              )}
                          </div>
                        </div>
                      ))
                    ))}
                  {activeTab === 'created' &&
                    (tasks.createdByMe.length === 0 ? (
                      <p style={styles.empty}>Нет созданных задач</p>
                    ) : (
                      tasks.createdByMe.map((task) => (
                        <div key={task.id} style={styles.taskCard}>
                          <div style={styles.taskHeader}>
                            <strong>{task.title}</strong>
                            <span
                              style={{
                                ...styles.statusBadge,
                                backgroundColor:
                                  statusColors[task.status] + '22',
                                color: statusColors[task.status],
                                border: `1px solid ${
                                  statusColors[task.status]
                                }44`,
                              }}
                            >
                              {statusLabels[task.status]}
                            </span>
                          </div>
                          {task.description && (
                            <p style={styles.taskDesc}>
                              {task.description}
                            </p>
                          )}
                          <div style={styles.taskMeta}>
                            <span>
                              <User size={12} style={{ marginRight: 4 }} />
                              {task.assigneeName}
                            </span>
                            <span>
                              <Clock size={12} style={{ marginRight: 4 }} />
                              {task.dueDate.split('-').reverse().join('.')}
                            </span>
                          </div>
                          {task.attachments.length > 0 && (
                            <div style={styles.taskAttachments}>
                              <Paperclip size={14} />
                              {task.attachments.map((att, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => downloadFile(att)}
                                  style={styles.attachmentLink}
                                >
                                  {att.name} (
                                  {(att.size / 1024).toFixed(1)} KB)
                                </button>
                              ))}
                            </div>
                          )}
                          {task.completionComment && (
                            <div style={styles.taskAnswer}>
                              <MessageSquare size={14} style={{ marginRight: 4 }} />
                              <strong>Комментарий исполнителя:</strong> {task.completionComment}
                            </div>
                          )}
                          {task.status === 'pending_review' && (
                            <div style={styles.taskActions}>
                              <button
                                onClick={() =>
                                  handleReview(task.id, 'approve')
                                }
                                style={{
                                  ...styles.actionButton,
                                  backgroundColor: '#28a745',
                                }}
                              >
                                <CheckCircle size={14} /> Принять
                              </button>
                              <button
                                onClick={() =>
                                  openCommentModal(task.id, 'reject')
                                }
                                style={{
                                  ...styles.actionButton,
                                  backgroundColor: '#ff4d4f',
                                }}
                              >
                                <XCircle size={14} /> Отклонить
                              </button>
                              <button
                                onClick={() =>
                                  openCommentModal(task.id, 'revision')
                                }
                                style={{
                                  ...styles.actionButton,
                                  backgroundColor: '#faad14',
                                }}
                              >
                                <RotateCcw size={14} /> Доработка
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    ))}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCreateForm(true);
                    }}
                    style={styles.addButton}
                  >
                    <Plus size={16} style={{ marginRight: '4px' }} />
                    Создать задачу
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <div style={styles.createForm}>
            <h4 style={styles.formTitle}>Новая задача</h4>
            <form onSubmit={handleCreateSubmit}>
              <input
                type="text"
                placeholder="Заголовок *"
                value={form.title}
                onChange={(e) =>
                  setForm({ ...form, title: e.target.value })
                }
                style={styles.input}
                required
              />
              <textarea
                placeholder="Описание"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                style={styles.textarea}
                rows={3}
              />
              <select
                value={form.assigneeId}
                onChange={(e) =>
                  setForm({ ...form, assigneeId: e.target.value })
                }
                style={styles.input}
                required
              >
                <option value="">Выберите исполнителя</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) =>
                  setForm({ ...form, dueDate: e.target.value })
                }
                style={styles.input}
                required
              />
              <div style={styles.attachments}>
                <label style={styles.attachmentsLabel}>
                  Вложения
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  id="task-attachments-input"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById('task-attachments-input')
                      ?.click()
                  }
                  style={styles.uploadButton}
                >
                  <Upload size={16} style={{ marginRight: '8px' }} />
                  Выбрать файл
                </button>
                {attachmentsPreview.length > 0 && (
                  <div style={styles.attachmentsList}>
                    {attachmentsPreview.map((file, idx) => (
                      <div key={idx} style={styles.attachmentItem}>
                        <span>
                          {file.name} (
                          {(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          style={styles.removeFile}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.formActions}>
                <button type="submit" style={styles.submitButton}>
                  Создать
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateForm(false);
                  }}
                  style={styles.cancelButton}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Модальное окно отправки на согласование */}
      {showReviewModal && (
        <div
          className="task-modal-container"
          style={styles.overlay}
          onClick={closeReviewModal}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.header}>
              <h3 style={styles.title}>
                <MessageSquare size={20} style={{ marginRight: 8 }} />
                Отправить на согласование
              </h3>
              <button onClick={closeReviewModal} style={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div style={styles.reviewForm}>
              <textarea
                placeholder="Комментарий о выполнении"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                style={styles.textarea}
                rows={3}
              />
              <div style={styles.attachments}>
                <label style={styles.attachmentsLabel}>
                  Прикрепить файлы
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleReviewFileChange}
                  id="review-attachments-input"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById('review-attachments-input')
                      ?.click()
                  }
                  style={styles.uploadButton}
                >
                  <Upload size={16} style={{ marginRight: '8px' }} />
                  Выбрать файл
                </button>
                {reviewAttachmentsPreview.length > 0 && (
                  <div style={styles.attachmentsList}>
                    {reviewAttachmentsPreview.map((file, idx) => (
                      <div key={idx} style={styles.attachmentItem}>
                        <span>
                          {file.name} (
                          {(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            removeReviewAttachment(idx)
                          }
                          style={styles.removeFile}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.formActions}>
                <button
                  onClick={handleSendForReview}
                  style={styles.submitButton}
                >
                  Отправить
                </button>
                <button
                  onClick={closeReviewModal}
                  style={styles.cancelButton}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для ввода комментария при отклонении/доработке */}
      {showCommentModal && (
        <div
          className="task-modal-container"
          style={styles.overlay}
          onClick={() => setShowCommentModal(false)}
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.header}>
              <h3 style={styles.title}>
                <MessageSquare size={20} style={{ marginRight: 8 }} />
                {commentAction === 'rejected' ? 'Отклонить задачу' : 'Отправить на доработку'}
              </h3>
              <button onClick={() => setShowCommentModal(false)} style={styles.closeButton}>
                <X size={20} />
              </button>
            </div>
            <div style={styles.reviewForm}>
              <textarea
                placeholder={
                  commentAction === 'rejected'
                    ? 'Укажите причину отклонения'
                    : 'Укажите, что нужно доработать'
                }
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                style={styles.textarea}
                rows={3}
              />
              <div style={styles.formActions}>
                <button
                  onClick={submitCommentAction}
                  style={{
                    ...styles.submitButton,
                    backgroundColor: commentAction === 'rejected' ? '#ff4d4f' : '#faad14',
                  }}
                >
                  {commentAction === 'rejected' ? 'Отклонить' : 'Отправить на доработку'}
                </button>
                <button
                  onClick={() => setShowCommentModal(false)}
                  style={styles.cancelButton}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Стили =====
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1150,
  },
  modal: {
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '28px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow:
      '0 25px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.4) inset',
    border: '1px solid rgba(255,255,255,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
  },
  closeButton: {
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255,255,255,0.5)',
    borderRadius: '10px',
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#666',
    transition: 'all 0.2s',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    paddingBottom: '10px',
  },
  tab: {
    padding: '8px 18px',
    background: 'rgba(255,255,255,0.5)',
    backdropFilter: 'blur(6px)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#666',
    transition: 'all 0.2s',
  },
  activeTab: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  empty: {
    textAlign: 'center',
    color: '#999',
    padding: '20px',
  },
  taskCard: {
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '16px',
    border: '1px solid rgba(255,255,255,0.5)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  },
  taskHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  statusBadge: {
    fontSize: '12px',
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: '14px',
    whiteSpace: 'nowrap',
  },
  taskDesc: {
    fontSize: '13px',
    color: '#555',
    marginBottom: '8px',
    lineHeight: 1.5,
  },
  taskMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    color: '#888',
    marginBottom: '8px',
    alignItems: 'center',
  },
  taskAttachments: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    fontSize: '12px',
    color: '#666',
    marginBottom: '10px',
  },
  attachmentLink: {
    background: 'rgba(37,117,252,0.08)',
    border: '1px solid rgba(37,117,252,0.15)',
    borderRadius: '12px',
    padding: '2px 8px',
    color: '#2575fc',
    cursor: 'pointer',
    fontSize: '12px',
    marginLeft: '4px',
  },
  taskActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
    flexWrap: 'wrap',
  },
  reviewActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 4px 10px rgba(37,117,252,0.2)',
    transition: 'all 0.2s',
  },
  addButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '8px 20px',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginTop: '8px',
    alignSelf: 'flex-start',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
    transition: 'all 0.2s',
  },
  createForm: {
    marginTop: '8px',
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '16px',
    color: '#1a1a2e',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(6px)',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.08)',
    background: 'rgba(255,255,255,0.6)',
    backdropFilter: 'blur(6px)',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    marginBottom: '12px',
    boxSizing: 'border-box',
  },
  attachments: {
    marginBottom: '12px',
  },
  attachmentsLabel: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#4a4a68',
  },
  uploadButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '8px 18px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 4px 10px rgba(37,117,252,0.2)',
    transition: 'all 0.2s',
  },
  attachmentsList: {
    marginTop: '12px',
  },
  attachmentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    padding: '6px 10px',
    backgroundColor: 'rgba(0,0,0,0.04)',
    borderRadius: '8px',
    marginBottom: '4px',
  },
  removeFile: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#ff4d4f',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white',
    border: 'none',
    borderRadius: '30px',
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(37,117,252,0.3)',
    transition: 'all 0.2s',
  },
  cancelButton: {
    background: 'transparent',
    border: '1px solid rgba(0,0,0,0.12)',
    color: '#666',
    borderRadius: '30px',
    padding: '10px 24px',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.2s',
  },
  reviewForm: {
    marginTop: '8px',
  },
  taskAnswer: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#333',
    background: 'rgba(255,215,0,0.1)',
    padding: '8px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
  },
};

export default TaskModal;